import { useState, useCallback } from 'react';
import { Check, X, Calendar, List, Type, Hash, ToggleLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import type { ExtractionField } from '@/features/export/hooks/useExport';

// =========================================
// DataEditor — inline cell editor by field type
// =========================================

interface DataEditorProps {
  field: ExtractionField;
  value: unknown;
  onSave: (value: unknown) => void;
  onCancel: () => void;
  className?: string;
}

export function DataEditor({
  field,
  value,
  onSave,
  onCancel,
  className,
}: DataEditorProps) {
  switch (field.type) {
    case 'text':
      return (
        <TextEditor
          value={value as string | null}
          onSave={onSave}
          onCancel={onCancel}
          className={className}
        />
      );
    case 'number':
      return (
        <NumberEditor
          value={value as number | null}
          onSave={onSave}
          onCancel={onCancel}
          className={className}
        />
      );
    case 'boolean':
      return (
        <BooleanEditor
          value={value as boolean | null}
          onSave={onSave}
          onCancel={onCancel}
          className={className}
        />
      );
    case 'list':
      return (
        <ListEditor
          value={value as string[] | null}
          onSave={onSave}
          onCancel={onCancel}
          className={className}
        />
      );
    case 'date':
      return (
        <DateEditor
          value={value as string | null}
          onSave={onSave}
          onCancel={onCancel}
          className={className}
        />
      );
    default:
      return (
        <TextEditor
          value={String(value ?? '')}
          onSave={onSave}
          onCancel={onCancel}
          className={className}
        />
      );
  }
}

// =========================================
// Field type icon
// =========================================

export function FieldTypeIcon({ type }: { type: ExtractionField['type'] }) {
  const icons: Record<string, typeof Type> = {
    text: Type,
    number: Hash,
    boolean: ToggleLeft,
    list: List,
    date: Calendar,
  };
  const Icon = icons[type] ?? Type;
  return <Icon className="h-3.5 w-3.5 text-slate-400" />;
}

// =========================================
// Display value (read-only cell)
// =========================================

interface DisplayValueProps {
  field: ExtractionField;
  value: unknown;
  className?: string;
}

export function DisplayValue({ field, value, className }: DisplayValueProps) {
  if (value === null || value === undefined || value === '') {
    return (
      <span className={cn('text-slate-400 italic text-xs', className)}>
        Empty
      </span>
    );
  }

  switch (field.type) {
    case 'boolean':
      return (
        <Badge
          variant={value ? 'default' : 'secondary'}
          className={cn('text-xs', className)}
        >
          {value ? 'Yes' : 'No'}
        </Badge>
      );
    case 'list':
      if (Array.isArray(value) && value.length > 0) {
        return (
          <div className={cn('flex flex-wrap gap-1', className)}>
            {(value as string[]).slice(0, 3).map((item, i) => (
              <Badge key={i} variant="outline" className="text-[10px] px-1.5">
                {item}
              </Badge>
            ))}
            {(value as string[]).length > 3 && (
              <span className="text-[10px] text-slate-400">
                +{(value as string[]).length - 3}
              </span>
            )}
          </div>
        );
      }
      return (
        <span className={cn('text-slate-400 italic text-xs', className)}>
          Empty list
        </span>
      );
    case 'number':
      return (
        <span className={cn('font-mono text-sm tabular-nums', className)}>
          {String(value)}
        </span>
      );
    case 'date':
      return (
        <span className={cn('text-sm', className)}>
          {String(value)}
        </span>
      );
    default:
      return (
        <span className={cn('text-sm line-clamp-2', className)}>
          {String(value)}
        </span>
      );
  }
}

// =========================================
// TextEditor
// =========================================

interface EditorBaseProps {
  onSave: (value: unknown) => void;
  onCancel: () => void;
  className?: string;
}

function TextEditor({
  value,
  onSave,
  onCancel,
  className,
}: EditorBaseProps & { value: string | null }) {
  const [draft, setDraft] = useState(value ?? '');

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSave(draft || null);
      } else if (e.key === 'Escape') {
        onCancel();
      }
    },
    [draft, onSave, onCancel]
  );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        className="h-7 text-xs"
      />
      <EditorActions onSave={() => onSave(draft || null)} onCancel={onCancel} />
    </div>
  );
}

// =========================================
// NumberEditor
// =========================================

function NumberEditor({
  value,
  onSave,
  onCancel,
  className,
}: EditorBaseProps & { value: number | null }) {
  const [draft, setDraft] = useState(value !== null ? String(value) : '');

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSave(draft === '' ? null : Number(draft));
      } else if (e.key === 'Escape') {
        onCancel();
      }
    },
    [draft, onSave, onCancel]
  );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Input
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        className="h-7 text-xs font-mono"
      />
      <EditorActions
        onSave={() => onSave(draft === '' ? null : Number(draft))}
        onCancel={onCancel}
      />
    </div>
  );
}

// =========================================
// BooleanEditor
// =========================================

function BooleanEditor({
  value,
  onSave,
  onCancel,
  className,
}: EditorBaseProps & { value: boolean | null }) {
  const [draft, setDraft] = useState(value ?? false);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Switch checked={draft} onCheckedChange={setDraft} />
      <span className="text-xs text-slate-600">{draft ? 'Yes' : 'No'}</span>
      <EditorActions onSave={() => onSave(draft)} onCancel={onCancel} />
    </div>
  );
}

// =========================================
// ListEditor
// =========================================

function ListEditor({
  value,
  onSave,
  onCancel,
  className,
}: EditorBaseProps & { value: string[] | null }) {
  const [draft, setDraft] = useState((value ?? []).join(', '));

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const items = draft
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        onSave(items.length > 0 ? items : null);
      } else if (e.key === 'Escape') {
        onCancel();
      }
    },
    [draft, onSave, onCancel]
  );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="item1, item2, ..."
        autoFocus
        className="h-7 text-xs"
      />
      <EditorActions
        onSave={() => {
          const items = draft
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onSave(items.length > 0 ? items : null);
        }}
        onCancel={onCancel}
      />
    </div>
  );
}

// =========================================
// DateEditor
// =========================================

function DateEditor({
  value,
  onSave,
  onCancel,
  className,
}: EditorBaseProps & { value: string | null }) {
  const [draft, setDraft] = useState(value ?? '');

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSave(draft || null);
      } else if (e.key === 'Escape') {
        onCancel();
      }
    },
    [draft, onSave, onCancel]
  );

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Input
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        className="h-7 text-xs"
      />
      <EditorActions onSave={() => onSave(draft || null)} onCancel={onCancel} />
    </div>
  );
}

// =========================================
// EditorActions — save / cancel buttons
// =========================================

function EditorActions({
  onSave,
  onCancel,
}: {
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-0.5 shrink-0">
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
        onClick={onSave}
      >
        <Check className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 text-slate-400 hover:text-slate-600"
        onClick={onCancel}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
